import cn from 'classnames'
import { BogoStickyFooterContent } from '@mc/bogo'
import { useBogo } from '@mc/bogo-utils'
import { Conditional, ShowIf } from '@mc/conditional-rendering'
import { FreeTrialStickyFooter, useFreeTrial } from '@mc/free-trial'
import { usePromotion } from '@mc/promo-utils'
import { PromoStickyFooterContent } from '@mc/promos'
import CTA from 'components/CTA'
import { trackPropsType } from 'components/CTA/events'
import { PricingText } from 'components/PricingText'
import type { StickyConfig } from 'components/Sticky/useSticky'
import { Pen7570StickyPricingTextWrapper } from 'experiments/pen-7570_v1_global_sticky_footer_all_classes_top_line'
import { useTrackPen7676Experiment } from 'experiments/pen-7676_v1_promo_sticky_footer_face_stack'
import { Instructors } from './Instructors'
import {
  useTrackStickyFooterViewed,
  STICKY_FOOTER_TYPES,
} from './useTrackStickyFooterViewed'

interface CTAProps {
  href?: string
  trackProps: trackPropsType
  onClick: (action: string) => void
  children: React.ReactNode
  fullWidth?: boolean
  className?: string
}

const HighlightedLayout = ({ children }: { children: React.ReactNode }) => (
  <div style={{ backgroundColor: 'var(--mc-color-pink-500)' }}>{children}</div>
)

export const StickyContent = ({
  ctaProps,
  stickyConfig,
}: {
  ctaProps: CTAProps
  stickyConfig?: StickyConfig
}) => {
  const { isBogoOfferEligible } = useBogo()
  const { isPromotionOn } = usePromotion()
  const { trial } = useFreeTrial()

  const getStickyFooterType = () => {
    if (trial.eligible) return STICKY_FOOTER_TYPES.FREE_TRIAL
    if (isPromotionOn) return STICKY_FOOTER_TYPES.PROMOTION
    if (isBogoOfferEligible) return STICKY_FOOTER_TYPES.BOGO

    return STICKY_FOOTER_TYPES.DEFAULT
  }

  const stickyFooterType = getStickyFooterType()

  useTrackStickyFooterViewed(stickyFooterType)
  useTrackPen7676Experiment(stickyFooterType, stickyConfig)

  const isHighlightedFooter = isPromotionOn || isBogoOfferEligible

  const renderSignUpCTA = (signUpCtaProps?: Partial<CTAProps>) => (
    <CTA
      href={ctaProps.href}
      trackProps={ctaProps.trackProps}
      onClick={ctaProps.onClick}
      kind={isHighlightedFooter ? 'play' : undefined}
      {...signUpCtaProps}
    >
      {ctaProps.children}
    </CTA>
  )

  return (
    <Conditional>
      <ShowIf condition={stickyFooterType === STICKY_FOOTER_TYPES.FREE_TRIAL}>
        <FreeTrialStickyFooter />
      </ShowIf>

      <ShowIf condition={stickyFooterType === STICKY_FOOTER_TYPES.PROMOTION}>
        <HighlightedLayout>
          <PromoStickyFooterContent
            SignUpCta={renderSignUpCTA}
            PricingText={() => <PricingText isStickyFooter />}
          />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf condition={stickyFooterType === STICKY_FOOTER_TYPES.BOGO}>
        <HighlightedLayout>
          <BogoStickyFooterContent
            SignUpCta={renderSignUpCTA}
            PricingText={() => <PricingText isStickyFooter />}
          />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf isDefault>
        <div
          className={cn(
            'container d-flex align-items-center mc-py-4',
            'justify-content-between flex-gap-4',
          )}
        >
          <Instructors>
            <Pen7570StickyPricingTextWrapper
              stickyConfig={stickyConfig}
              control={
                <PricingText translationKey='longStyled' isStickyFooter>
                  <span className='mc-text--bold' />
                  <span className='d-block mc-opacity--muted' />
                </PricingText>
              }
            />
          </Instructors>
          {renderSignUpCTA()}
        </div>
      </ShowIf>
    </Conditional>
  )
}
